/**
 * The machine-first Workers board, decided away from the drawing.
 *
 * Workers belong under the computer that runs them. A Worker with no current project or
 * task stays under its computer and is marked Unassigned on its own card; there is no
 * separate Unassigned group. Only a Worker whose canonical record names no computer at
 * all is set apart, and it is labelled for what it is: not attached to a computer.
 *
 * Pure in, pure out. Nothing here opens, closes or routes to a Worker or a terminal.
 */
import type { CockpitWorkerCard } from './cockpit-view';
import type { WorkerChatReadiness } from './worker-chat-readiness';

/** The Worker's own state. Kept apart from whether it has work, so neither hides the other. */
export type WorkerBoardState = 'working' | 'idle' | 'offline' | 'attention' | 'unknown';

/** Whether the Worker currently has a project or task. Unassigned is not a status. */
export type WorkerAssignment = 'assigned' | 'unassigned';

const WORKING = new Set(['working', 'running', 'starting', 'thinking', 'processing', 'busy']);
const IDLE = new Set(['idle', 'completed', 'available', 'ready']);
const OFFLINE = new Set(['offline', 'disconnected', 'retired']);
const ATTENTION = new Set([
  'failed', 'error', 'exited', 'blocked', 'awaiting_approval',
  'waiting_for_approval', 'waiting_for_input',
]);

/** What the Worker is doing, from its own status and anything waiting on the owner. */
export function workerBoardState(card: Pick<CockpitWorkerCard, 'status' | 'attention'>): WorkerBoardState {
  const status = card.status.trim().toLowerCase();
  if (OFFLINE.has(status)) return 'offline';
  if (card.attention !== null || ATTENTION.has(status)) return 'attention';
  if (WORKING.has(status)) return 'working';
  if (IDLE.has(status)) return 'idle';
  return 'unknown';
}

export const WORKER_BOARD_STATE_LABEL: Readonly<Record<WorkerBoardState, string>> = {
  working: 'Working',
  idle: 'Idle',
  offline: 'Offline',
  attention: 'Needs attention',
  unknown: 'Status unknown',
};

export function workerAssignment(card: Pick<CockpitWorkerCard, 'project' | 'activity'>): WorkerAssignment {
  return card.project === null && card.activity === null ? 'unassigned' : 'assigned';
}

/**
 * The computer each card belongs to, from canonical readiness when the terminal row does
 * not name one (a Worker with no open terminal still has a workforce session on a node).
 * A card is never moved to a computer that no record names; its project is filled the
 * same way. Everything else on the card is left exactly as it was.
 */
export function withCanonicalMachine<T extends CockpitWorkerCard>(
  cards: readonly T[],
  readiness: readonly Pick<WorkerChatReadiness, 'workerId' | 'nodeId' | 'nodeName' | 'projectName'>[],
): T[] {
  const byWorker = new Map(readiness.map((worker) => [worker.workerId, worker]));
  return cards.map((card) => {
    const known = byWorker.get(card.workerId);
    if (known === undefined) return card;
    const machineId = card.machineId ?? known.nodeId;
    const machine = card.machine ?? (card.machineId === null ? known.nodeName : null);
    const project = card.project ?? known.projectName;
    if (machineId === card.machineId && machine === card.machine && project === card.project) return card;
    return { ...card, machineId, machine, project };
  });
}

export interface WorkerMachineGroup<T> {
  /** The computer's node id, or null for the exceptional not-attached group. */
  readonly machineId: string | null;
  readonly name: string;
  /** True only for Workers whose canonical record names no computer at all. */
  readonly unattached: boolean;
  readonly cards: readonly T[];
}

export const UNATTACHED_MACHINE_LABEL = 'Not attached to a computer';

/**
 * Workers grouped under their computer, computers in the order their first Worker appears
 * (roster order), Workers in roster order within each. The not-attached group, when there
 * is one, always comes last.
 */
export function workersByMachine<T extends Pick<CockpitWorkerCard, 'machineId' | 'machine'>>(
  cards: readonly T[],
  machineNames: ReadonlyMap<string, string> = new Map(),
): readonly WorkerMachineGroup<T>[] {
  const groups = new Map<string, T[]>();
  const unattached: T[] = [];
  for (const card of cards) {
    if (card.machineId === null) {
      unattached.push(card);
      continue;
    }
    const current = groups.get(card.machineId) ?? [];
    current.push(card);
    groups.set(card.machineId, current);
  }
  const attached = [...groups.entries()].map(([machineId, grouped]) => ({
    machineId,
    name: machineNames.get(machineId) ?? grouped.find((card) => card.machine !== null)?.machine ?? 'Unnamed computer',
    unattached: false,
    cards: grouped,
  }));
  return unattached.length === 0
    ? attached
    : [...attached, { machineId: null, name: UNATTACHED_MACHINE_LABEL, unattached: true, cards: unattached }];
}

/** The last folder of a working directory, which is the worktree/branch folder in practice. */
export function folderLabel(cwd: string | null): string | null {
  if (cwd === null) return null;
  const parts = cwd.split(/[\\/]+/).filter((part) => part !== '');
  return parts.at(-1) ?? null;
}
